import { IGalleryMedia, IPoi } from "~/interfaces";
import { getDetailImage } from "./cdn";
import { updateMyReviewFromDeletedCache } from "~/util/reviews-cache";
import { IState } from "~/state/store";
import { DEFAULT_DETAIL_STATE } from "~/state/detail/constants";
import Scene from "~/scene";
import SceneCoords from "~/scene/common/coords";
import ScenePixel from "~/scene/common/pixel";
import { IDetailState } from "~/state/detail/interfaces";
import SceneGeometrySmartMulti from "~/scene/geometry/smartmulti";

export function getTitle(data: any = null, likeData: any = null): string {
	if (likeData && "usertitle" in likeData) {
		return likeData.usertitle === "" ? likeData.title : likeData.usertitle;
	}

	return data?.title || "";
}

export function parsePhone({ number: phoneNumber, countrycode }: {
	number: string;
	countrycode?: string;
}, replaceSign = "&nbsp;"): {
	number: string;
	wholeNumber: string;
} {
	let number = phoneNumber;
	const wholeNumber = (countrycode ? countrycode : "") + number;

	if (number.length >= 12 && number.substring(0, 3) === "420") {
		number = number.substring(3);
	}
	// eslint-disable-next-line require-unicode-regexp
	number = number.replace(/(\d{1,3}(?=(\d{3})+(?!\d)))/g, `$1${replaceSign}`);

	if (countrycode) {
		number = `${countrycode} ${number}`;
	}

	return {
		number,
		wholeNumber,
	};
}

interface IParseGallery {
	best: IGalleryMedia;
	gallery: Array<IGalleryMedia>;
	webcam: Array<IGalleryMedia>;
	preferWebcam: boolean;
}

export function parseGallery(data: IPoi): Promise<IParseGallery> {
	return new Promise(resolve => {
		const output: IParseGallery = {
			best: null,
			gallery: [],
			webcam: null,
			preferWebcam: false,
		};
		const isBooking = !!data.extend.poi.booking;

		if (data.gallery && data.gallery.length) {
			data.gallery.forEach(gallery => {
				if (!("sequence" in gallery) || gallery.sequence === 0) {
					// best fotka
					const galleryMedia = gallery.media || [];

					// galerie
					output.gallery = [...gallery.media];

					galleryMedia.every(media => {
						if (media.best) {
							output.best = media;

							return false;
						}

						return true;
					});
				} else {
					// webcam
					output.webcam = [...gallery.media];

					if (gallery.prefered === 1) {
						output.preferWebcam = true;
					}
				}
			});
		}

		if (!output.best) {
			if (output.gallery && output.gallery.length) {
				output.best = output.gallery[0];
			}
		}

		if (isBooking && output.gallery && output.gallery.length) {
			// musime zkusit projit obrazky, protoze nektere muzou vracet 404
			// prvni fotka - tu preskocime
			let correctedGallery = [output.gallery.shift()];
			const allImages: Array<Promise<void>> = [];

			output.gallery.forEach((img, index) => {
				allImages.push(new Promise(resolveInner => {
					const url = getDetailImage(img.urls["1x1"]);

					if (url) {
						const image = new Image();

						image.crossOrigin = "anonymous";
						image.addEventListener("load", () => {
							correctedGallery[index + 1] = img;
							resolveInner();
						});
						image.addEventListener("error", () => {
							correctedGallery[index + 1] = null;
							resolveInner();
						});
						image.src = url;
					} else {
						resolveInner();
					}
				}));
			});


			Promise.all(allImages).then(() => {
				// odstranime prazdne
				correctedGallery = correctedGallery.filter(img => !!img);

				output.gallery = correctedGallery;
				if (correctedGallery.length) {
					output.best = correctedGallery[0];
				}

				resolve(output);
			}, () => {});
		} else {
			resolve(output);
		}
	});
}

export function postProcessPOI(poi: IPoi) {
	// prevzato z puvodniho _postProcess v detailu

	if (!poi.extend) {
		poi.extend = {};
	}
	if (!poi.extend.poi) {
		poi.extend.poi = {};
	}

	// titulkovac data
	if (!poi.titleVars) {
		poi.titleVars = {
			locationMain1: "",
			locationMain2: [],
			locationMain3: [],
			locationSub1: "",
			locationSub2: [],
			locationSub3: [],
			specification: "",
			titleMain: "",
			titleSub: "",
			titleMapMain: "",
			titleMapSub: "",
			titleSmartMain: "",
			titleSmartSub: "",
		};
	}

	poi.title = poi.titleVars.titleMain || poi.title;
	poi.typeName = poi.titleVars.specification || poi.typeName;

	// rozsireni od firem - url pro spravu
	if (poi.admin_urls) {
		if (!poi.extend.contact) {
			poi.extend.contact = {};
		}

		if (poi.admin_urls.regflow) {
			poi.extend.contact.firmy = {
				value: [poi.admin_urls.regflow],
			};
		}
	}

	if (poi.poilink && poi.poilink.value && poi.poilink.value.length) {
		const poiLinkValue = poi.poilink.value;

		for (let index = 0; index < poiLinkValue.length; index++) {
			if (poiLinkValue[index].review) {
				poi.review = {
					...poiLinkValue[index].review,
					id: poi.id as number,
					source: poi.source,
				};
				break;
			}
		}
	}

	// datum v extend.offers.result[].create/date_from/date_to je fakt Date ze serveru, zmenime na ISO string
	if (poi.offers?.result?.length) {
		for (let index = 0; index < poi.offers.result.length; index++) {
			poi.offers.result[index].created = (poi.offers.result[index].created as unknown as Date).toISOString();
			if (poi.offers.result[index].date_from) {
				// eslint-disable-next-line camelcase
				poi.offers.result[index].date_from = (poi.offers.result[index].date_from as unknown as Date).toISOString();
			}
			if (poi.offers.result[index].date_to) {
				// eslint-disable-next-line camelcase
				poi.offers.result[index].date_to = (poi.offers.result[index].date_to as unknown as Date).toISOString();
			}
		}
	}

	// upravy pro hodnoceni
	updateMyReviewFromDeletedCache(poi);

	if (poi.myReview && Array.isArray(poi.myReview)) {
		poi.myReview = poi.myReview.filter(item => item.state === "ok")[0] || null;
	}

	if (poi.extend.poi.alternatives?.length) {
		poi.review.alternatives = poi.extend.poi.alternatives;
	}

	// upravy pro windy webcam
	if (poi.source === "wcam") {
		if (poi.extend.photo) {
			delete poi.extend.photo;
		}

		if (poi.gallery) {
			delete poi.gallery;
		}
	}
}

export function isDetailActive(state: IState): boolean {
	return state.detail.id !== DEFAULT_DETAIL_STATE.id && state.detail.source !== DEFAULT_DETAIL_STATE.source;
}

export function isPhotoDetail(state: IState): boolean {
	const { data } = state.detail;

	return !!(data && (data.source === "foto" || data.source === "ffoto") && !Mapy.getComponent("gallery").isActive());
}

interface IDetailComputeCenterZoom {
	poi: IPoi;
	showZ?: string;
}

export function detailComputeCenterZoom({
	poi,
	showZ = "",
}: IDetailComputeCenterZoom): IDetailState["centerZoom"] {
	let cz: [SceneCoords, number] = [null, 0];
	let geometry: SceneGeometrySmartMulti = null;
	let geomBbox = null;

	if (poi.geom) {
		const geom = poi.geom;

		if (geom && geom.type && geom.type !== "multipoint") {
			geometry = Scene.createGeometrySmartMulti(poi);

			const calcGeomBbox = geometry.getBBoxWgs84();
			const min = calcGeomBbox.min;
			const max = calcGeomBbox.max;

			geomBbox = [[min.x, min.y], [max.x, max.y]];
		}
	}

	/* vzit center/zoom z geometrie */
	if (geometry && geometry.getCoords()) {
		cz = geometry.computeCenterZoom(true) as [SceneCoords, number];

		/* pokud je geometrie bodova */
		if (cz[1] === Infinity) {
			cz[1] = 17;
		}
	/* vzit center/zoom z marku a sourcu */
	} else {
		cz = [Scene.coordsFromWGS84(poi.mark.lon, poi.mark.lat), Mapy.Util.computeZoom(poi)];

		if (showZ) {
			cz[1] = parseInt(showZ);
		}
	}

	const finalCoords = cz[0].toWGS84();
	const zoom = cz[1];
	// prepocet bbox podle vypocitane souradnice + zoomu
	const centerPx = Scene.coordsToPixel(cz[0]);
	const size = Scene.getSize();
	const width = Math.round(size.x / 2);
	const height = Math.round(size.y / 2);
	const lb = Scene.pixelToCoords(centerPx.clone().plus(new ScenePixel(-width, height))).toWGS84();
	const rt = Scene.pixelToCoords(centerPx.clone().plus(new ScenePixel(width, -height))).toWGS84();

	/* debug mcoords
	console.log(`BOX(${lb[0]} ${lb[1]}, ${rt[0]} ${rt[1]})`);
	console.log(`POINT(${cz[0].toWGS84()[0]} ${cz[0].toWGS84()[1]})`);
	geomBbox && console.log(`BOX(${geomBbox[0][0]} ${geomBbox[0][1]}, ${geomBbox[1][0]} ${geomBbox[1][1]})`);
	*/

	return {
		lon: finalCoords[0],
		lat: finalCoords[1],
		bbox: [lb, rt],
		geomBbox,
		zoom,
	};
}
